
// AUTO-GENERATED SDK for HeldShareRebalanceResult
import { Injectable } from '@angular/core';
import { GraphqlApiService } from '../../api/graphql-api.service';
import { HeldShareRebalanceResultBuilder } from './held-share-rebalance-result.builder';
import { HeldShareRebalanceResultSql } from './held-share-rebalance-result.sqlbuilder';
import type { ApiResponse } from '../../api/types';
import type { HeldShareRebalanceResultPoco } from '@libs/graphql/models';

@Injectable({ providedIn:'root' })
export class HeldShareRebalanceResultSdk {

  constructor(private api: GraphqlApiService) {}

  // ============================================================
  // SAFE JSON
  // ============================================================
  private safeJSON(v: any) {
    try { return JSON.stringify(v); }
    catch { return '{}'; }
  }

  // ============================================================
  // SAFE RESPONSE WRAPPER
  // ============================================================
  private wrap<T>(raw: any, key: string): ApiResponse<T> {
    const node = raw[key] ?? {};
    return {
      success: node.success ?? false,
      message: node.message ?? '',
      error: node.error ?? '',
      totalRecord: node.totalRecord ?? 0,
      data: (node.data ?? []) as T
    };
  }

  // ============================================================
  // SAFE SELECT
  // ============================================================
  private safeSelect(fields: string[]) {
    return fields.length
      ? fields.map(f => ({ columnName: f, alias: f, isTrusted: true }))
      : [{ columnName: '*', alias: '*', isTrusted: true }];
  }

  // ============================================================
  // FILTER BUILDER
  // ============================================================
  private cf(f: any): any[] {
    if (!f || typeof f !== 'object') return [];

    if (f.AND)
      return [{
        logic: 'And',
        isTrusted: false,
        subFilters: f.AND.map((x: any) => this.cf(x)[0])
      }];

    if (f.OR)
      return [{
        logic: 'Or',
        isTrusted: false,
        subFilters: f.OR.map((x: any) => this.cf(x)[0])
      }];

    const opMap: any = {
      eq: '=', ne: '<>', like: 'LIKE',
      gt: '>', gte: '>=', lt: '<', lte: '<=',
      in: 'IN', notIn: 'NOT IN'
    };

    const rows: any[] = [];

    for (const k of Object.keys(f)) {
      const v = f[k];

      if (typeof v !== 'object') {
        rows.push({
          column: k,
          operator: '=',
          value: v,
          logic: 'And',
          subFilters: [],
          isTrusted: false
        });
      } else {
        const op = Object.keys(v)[0];
        rows.push({
          column: k,
          operator: opMap[op] ?? op,
          value: v[op],
          logic: 'And',
          subFilters: [],
          isTrusted: false
        });
      }
    }

    return rows;
  }

  // ============================================================
  // LIST MODE
  // ============================================================
  async list(
    filter: any = {},
    sorts: any[] = [],
    fields: string[] = [],
    page = 1,
    pageSize = 50,
    options: { strict?: boolean, useCache?: boolean, debug?: boolean } = {}
  ): Promise<ApiResponse<HeldShareRebalanceResultPoco[]>> {

    const gql = `query ListHeldShareRebalanceResult($input: DynamicQueryJsonInput) {
      listHeldShareRebalanceResult(input: $input) {
        success
        message
        error
        totalRecord
        data {
          ${fields.length ? fields.join('\n          ') : 'id'}
        }
      }
    }`;

    const where = this.cf(filter);
    const selects = this.safeSelect(fields);
    const safeSorts = sorts && sorts.length
      ? sorts
      : [{ column: 'id', desc: true, isTrusted: true }];

    const raw = await this.api.request<any>(gql, {
      input: {
        queryJson: this.safeJSON({
          baseTable: 'HeldShareRebalanceResult',
          baseAlias: 'heldShareRebalanceResult',
          selects,
          joins: [],
          where,
          having: [],
          sorts: safeSorts,
          groupBy: [],
          skip: (page - 1) * pageSize,
          take: pageSize,
          options: {
            strictMode: options.strict ?? false,
            useCache: options.useCache ?? false
          }
        })
      }
    });

    return this.wrap<HeldShareRebalanceResultPoco[]>(raw, 'listHeldShareRebalanceResult');
  }

  // ============================================================
  // QUERY MODE (GraphQL builder)
  // ============================================================
  async query(
    b: (q: HeldShareRebalanceResultBuilder) => HeldShareRebalanceResultBuilder,
    filter: any = {},
    sorts: any[] = [],
    page = 1,
    pageSize = 50,
    options: { strict?: boolean } = {}
  ): Promise<ApiResponse<HeldShareRebalanceResultPoco[]>> {

    const q = new HeldShareRebalanceResultBuilder();
    b(q);

    const selects = q.getSelections().map(x => ({
      columnName: x,
      alias : x,
      isTrusted: true
    }));

    const gql = `query QHeldShareRebalanceResult($input: DynamicQueryJsonInput) {
      listHeldShareRebalanceResult(input: $input) {
        success
        message
        error
        totalRecord
        data
          ${q.toSelectionSet()}
      }
    }`;

    const raw = await this.api.request<any>(gql, {
      input: {
        queryJson: this.safeJSON({
          baseTable: 'HeldShareRebalanceResult',
          baseAlias: 'heldShareRebalanceResult',
          selects,
          joins: [],
          where: this.cf(filter),
          having: [],
          sorts,
          groupBy: [],
          skip: (page - 1) * pageSize,
          take: pageSize,
          options: { strictMode: options.strict ?? false }
        })
      }
    });

    return this.wrap<HeldShareRebalanceResultPoco[]>(raw, 'listHeldShareRebalanceResult');
  }

  // ============================================================
  // SQL MODE (SQL AST)
  // ============================================================
  async sql(
    fn: (q: HeldShareRebalanceResultSql) => void | HeldShareRebalanceResultSql,
    options: { strict?: boolean, debug?: boolean } = {}
  ): Promise<ApiResponse<HeldShareRebalanceResultPoco[]>> {

    const instance = new HeldShareRebalanceResultSql();
    const result = fn(instance) || instance;
    const q = result.build();

    q.options = q.options ?? {};
    q.options.strictMode = options.strict ?? false;

    const gql = `query SqlHeldShareRebalanceResult($input: DynamicQueryJsonInput) {
      listHeldShareRebalanceResult(input: $input) {
        success
        message
        error
        totalRecord
        data { id }
      }
    }`;

    const raw = await this.api.request<any>(gql, {
      input: { queryJson: this.safeJSON(q) }
    });

    return this.wrap<HeldShareRebalanceResultPoco[]>(raw, 'listHeldShareRebalanceResult');
  }

  // ============================================================
  // SAVE
  // ============================================================
  async save(item: any): Promise<ApiResponse<HeldShareRebalanceResultPoco>> {
    const gql = `mutation SaveHeldShareRebalanceResult($input: UpsertInputOfHeldShareRebalanceResultPocoInput!) {
      saveHeldShareRebalanceResult(input: $input) {
        success
        message
        error
      }
    }`;

    // UpsertInput requires BOTH item and items (items: [X!]! is non-null).
    // Send an empty items array when saving a single record.
    const raw = await this.api.request<any>(gql, { input: { item, items: [] } });

    return {
      success: raw.saveHeldShareRebalanceResult?.success ?? false,
      message: raw.saveHeldShareRebalanceResult?.message ?? '',
      error: raw.saveHeldShareRebalanceResult?.error ?? '',
      data: item
    };
  }

  // ============================================================
  // DELETE
  // ============================================================
  async delete(id: number, hardDelete=false): Promise<ApiResponse<boolean>> {
    const gql = `mutation DeleteHeldShareRebalanceResult($id: Long!, $hardDelete: Boolean!) {
      deleteHeldShareRebalanceResult(id: $id, hardDelete: $hardDelete) {
        success
        message
        error
      }
    }`;

    const raw = await this.api.request<any>(gql, { id, hardDelete });

    return {
      success: raw.deleteHeldShareRebalanceResult?.success ?? false,
      message: raw.deleteHeldShareRebalanceResult?.message ?? '',
      error: raw.deleteHeldShareRebalanceResult?.error ?? '',
      data: raw.deleteHeldShareRebalanceResult?.success ?? false
    };
  }
}
